import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'

const sizes = {
  sm: 'size-20',
  md: 'size-28',
}

export function ProgressRing({
  'aria-label': ariaLabel,
  children,
  className,
  max = 100,
  size = 'md',
  value,
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {
  'aria-label': string
  children?: ReactNode
  max?: number
  size?: keyof typeof sizes
  value: number
}) {
  const safeMax = Number.isFinite(max) && max > 0 ? max : 1
  const safeValue = Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), safeMax)
  const percentage = (safeValue / safeMax) * 100

  return (
    <div
      {...props}
      aria-label={ariaLabel}
      aria-valuemax={safeMax}
      aria-valuemin={0}
      aria-valuenow={safeValue}
      data-slot="progress-ring"
      role="progressbar"
      className={cn('relative grid shrink-0 place-items-center', sizes[size], className)}
    >
      <svg aria-hidden="true" viewBox="0 0 36 36" className="absolute inset-0 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" className="stroke-muted" />
        <circle
          cx="18"
          cy="18"
          r="15.5"
          fill="none"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={100 - percentage}
          strokeLinecap="round"
          strokeWidth="3"
          className="stroke-action motion-safe:transition-[stroke-dashoffset]"
        />
      </svg>
      <div className="relative grid place-items-center text-center">{children}</div>
    </div>
  )
}
